import { apiClient } from '@/config/api';
import { dispatchNotificationRoute } from '@/services/notificationRouter';

/**
 * Web build of the push service (overrides `pushNotifications.ts` via the
 * `.web.ts` resolver). Native Expo push has no browser equivalent, so this
 * implements the **Web Push** standard (Service Worker + VAPID, RFC 8291):
 *
 *   1. `registerForPushNotifications` — ask permission, subscribe through the
 *      already-registered service worker using the backend's VAPID public key
 *      (`GET /chat/vapid-public-key`), and register the resulting
 *      PushSubscription with `POST /chat/devices` as `platform: 'WEB'`.
 *   2. `registerNotificationListeners` — the service worker delivers a tap via
 *      `postMessage({source:'sga-push', data})` (open tab) or a `?push=<data>`
 *      cold-start param; both are funnelled into the shared
 *      `dispatchNotificationRoute` so routing matches native exactly.
 *
 * Everything is best-effort: unsupported browser, denied permission, or a
 * failed request degrades to a no-op (the app still works, just without push).
 */

// Kept for API parity with the native module (unused on web).
export const EAS_PROJECT_ID = '5d6efd67-665e-40e7-bc04-6d7646ace1cc';

/** No-op on web: the service worker's `showNotification` handles presentation. */
export const configureNotificationHandler = (): void => {
  /* intentionally empty */
};

const isSupported = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/** Decode a base64url VAPID key into the Uint8Array `applicationServerKey` wants. */
const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
};

/**
 * Subscribe this browser to Web Push and register it with the backend. MUST be
 * called only when authenticated (`/chat/*` is JWT-gated). Returns the
 * stringified PushSubscription on success, or null when unsupported, denied, or
 * unable to complete. Never throws.
 */
export const registerForPushNotifications = async (): Promise<string | null> => {
  try {
    if (!isSupported()) {
      return null;
    }

    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    if (permission !== 'granted') {
      return null;
    }

    const registration = await navigator.serviceWorker.ready;

    // Reuse an existing subscription; otherwise create one with the VAPID key.
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      const { publicKey } = await apiClient.get<{ publicKey: string }>(
        '/chat/vapid-public-key'
      );
      if (!publicKey) {
        return null;
      }
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    const pushToken = JSON.stringify(subscription);
    await apiClient.post('/chat/devices', { platform: 'WEB', pushToken });
    return pushToken;
  } catch {
    // Best-effort: a permission race, offline, or a 401 must not crash the app.
    return null;
  }
};

/**
 * Wire notification-tap routing for web. Mirrors the native
 * `registerNotificationListeners`: handles the cold-start tap (the service
 * worker opened the app with `?push=<json>`) and live taps (the service worker
 * `postMessage`s the focused tab). `getIsAuthenticated` is read at callback
 * time so the listener always sees current auth state. Returns a cleanup fn.
 */
export const registerNotificationListeners = (
  getIsAuthenticated: () => boolean
): (() => void) => {
  // Cold start: the SW may have launched us with the payload in the URL.
  try {
    const push = new URLSearchParams(window.location.search).get('push');
    if (push) {
      dispatchNotificationRoute(JSON.parse(push), getIsAuthenticated());
      // Strip it so a refresh doesn't re-route.
      window.history.replaceState({}, '', window.location.pathname);
    }
  } catch {
    /* ignore a malformed/absent param */
  }

  // Live tap: the SW posts {source:'sga-push', data} to focused clients.
  const onMessage = (event: MessageEvent) => {
    const payload = event.data;
    if (payload && payload.source === 'sga-push') {
      dispatchNotificationRoute(payload.data, getIsAuthenticated());
    }
  };
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', onMessage);
  }
  return () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    }
  };
};
